import { SportBanner } from "../types";

/**
 * The shape of a sportsbook banner: 1450 × 300, and 1450 × 600 on a phone,
 * where the slider switches under the `sm` breakpoint with the rest of the
 * sport layout. The game pages' banners are a little taller (1450 × 350 / 700).
 */
export const SPORT_BANNER_ASPECT_RATIO = "1450 / 300";
export const SPORT_BANNER_ASPECT_RATIO_MOBILE = "1450 / 600";

/**
 * The artwork the status pages ship with, shown while no banner is managed
 * for them in admin (a sidebar item with the page's URL, see
 * `usePageBanners`). Each piece carries its own title, so nothing is printed
 * over it.
 */
export const IN_PLAY_BANNERS = ["/inplay-sports.webp", "/football.webp", "/basketball.webp"];
export const NEXT24_BANNERS = ["/next24.webp"];
export const UPCOMING_BANNERS = ["/upcoming.webp"];

/**
 * The artwork made for each sport, by the provider's slug. The API lists
 * football, basketball and baseball today; the rest wait for their sport to
 * be switched on. A sport with none falls back to its stock photo with its
 * name printed over it (`getSportBanner`).
 */
const SPORT_ARTWORK: Record<string, string[]> = {
  "american-football": ["/american-football.webp"],
  "aussie-rules": ["/aussie-rules.webp"],
  baseball: ["/baseball.webp"],
  basketball: ["/basketball.webp"],
  boxing: ["/boxing.webp"],
  cricket: ["/cricket.webp"],
  darts: ["/darts.webp"],
  esports: ["/esports.webp"],
  football: ["/football.webp"],
  hockey: ["/ice-hockey.webp"],
  "ice-hockey": ["/ice-hockey.webp"],
  mma: ["/mma.webp"],
  rugby: ["/rugby.webp"],
  snooker: ["/snooker.webp"],
  soccer: ["/football.webp"],
  "table-tennis": ["/table-tennis.webp"],
  tennis: ["/tennis.webp"],
  volleyball: ["/volleyball.webp"],
  "water-polo": ["/water-polo.webp"],
};

export const getSportArtwork = (slug?: string): string[] => (slug && SPORT_ARTWORK[slug]) || [];

/** The stock photos: the fallback banner background, and the event page's backdrop. */
const SPORT_BANNERS: Record<string, SportBanner> = {
  football: {
    background: "/assets/banners/soccer-banner.webp",
    image: "/assets/banners/soccer-banner2.webp",
  },
  soccer: {
    background: "/assets/banners/soccer-banner.webp",
    image: "/assets/banners/soccer-banner2.webp",
  },
  basketball: {
    background: "/assets/banners/basketball-banner.webp",
    image: "/assets/banners/basketball-banner2.webp",
  },
  baseball: {
    background: "/assets/banners/baseball-banner.webp",
    image: "/assets/banners/baseball-banner2.webp",
  },
  volleyball: {
    background: "/assets/banners/volleyball-banner.webp",
    image: "/assets/banners/volleyball-banner-2.webp",
  },
  esports: {
    background: "/assets/banners/esports-banner.webp",
    image: "/assets/banners/esports-banner.webp",
  },
  "table-tennis": {
    background: "/assets/banners/table-game-banner.webp",
    image: "/assets/banners/table-game-banner2.webp",
  },
  rugby: {
    background: "/assets/banners/american-football-banner.webp",
    image: "/assets/banners/american-football-banner2.webp",
  },
  "american-football": {
    background: "/assets/banners/american-football-banner.webp",
    image: "/assets/banners/american-football-banner2.webp",
  },
  "ice-hockey": {
    background: "/assets/banners/ice-hockey-banner.webp",
    image: "/assets/banners/ice-hockey-banner.webp",
  },
  boxing: {
    background: "/assets/banners/mma-banner.webp",
    image: "/assets/banners/mma-banner2.webp",
  },
  bowling: {
    background: "/assets/banners/bowling-banner.webp",
    image: "/assets/banners/bowling-banner2.webp",
  },
};

const DEFAULT_BANNER: SportBanner = {
  background: "/assets/banners/soccer-banner.webp",
  image: "/assets/banners/soccer-banner2.webp",
};

export const getSportBanner = (slug?: string): SportBanner =>
  (slug && SPORT_BANNERS[slug]) || DEFAULT_BANNER;
